import { Component, Input, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import {
  compareStr,
  ConfirmDialogService,
  DataCheckerService,
  Destroyable,
  selectUsers,
  untilDestroy,
  User,
} from '@tokeet-frontend/tv3-platform'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { TaskChecklistItem, TaskResponse } from '@tokeet-frontend/tasks'
import * as fromRoot from '@tv3/store/state'
import * as lodash from 'lodash'
import { UserGuard } from '@tv3/guards/user.guard'
import { map, switchMap } from 'rxjs/operators'
import { selectTaskChecklists } from '@tokeet-frontend/tasks'
import * as moment from 'moment'
import { AuthService } from '@tv3/services/auth.service'
export type TaskChecklistFormData = {
  items: TaskChecklistItem[]
}

export function createTaskChecklistForm(fb: FormBuilder) {
  const form = fb.group({
    items: fb.array([]),
  })

  return form
}

export function createTaskChecklistItemForm(fb: FormBuilder, item?: TaskChecklistItem) {
  const data: Partial<TaskChecklistItem> = item || {}

  const form = fb.group({
    name: [data.name || '', [Validators.required]],
    description: [data.description || '', []],
    status: [data.status ?? 0, []],
    date_completed: [data.date_completed ?? undefined, []],
    completed_by: [data.completed_by ?? undefined, []],
  })

  return form
}

export function setTaskChecklistFormData(fb: FormBuilder, form: FormGroup, task: Partial<TaskResponse>) {
  const items = form.get('items') as FormArray

  items.clear()

  task.checklist?.forEach((item) => {
    if (item) items.push(createTaskChecklistItemForm(fb, item))
  })
}

export function getTaskChecklistFormData(form: FormGroup) {
  const data = form.getRawValue() as TaskChecklistFormData

  const items: TaskChecklistItem[] = []

  data.items?.forEach((item) => {
    if (!item || !item.name) return

    items.push({
      name: item.name,
      description: item.description || '',
      status: item.status || 0,
      date_completed: item.date_completed || 0,
      completed_by: item.completed_by || '',
    })
  })

  return items
}

@Component({
  selector: 'app-task-checklist',
  templateUrl: './task-checklist.component.html',
  styleUrls: ['./task-checklist.component.scss'],
})
export class TaskChecklistComponent extends Destroyable implements OnInit {
  @Input() form: FormGroup

  tableType = TableType.TaskChecklist

  users: Record<string, User> = {}

  taskCtrl = new FormControl('')
  checklists$ = this.taskCtrl.valueChanges.pipe(
    switchMap((v) =>
      this.store.pipe(
        select(selectTaskChecklists),
        map((items) => {
          if (!v) return items
          v = lodash.toLower(v)
          return lodash.filter(items, (t) => lodash.includes(lodash.toLower(t), v))
        })
      )
    )
  )

  isAdding = false

  constructor(
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private confirm: ConfirmDialogService,
    private auth: AuthService,
    dataChecker: DataCheckerService
  ) {
    super()

    dataChecker.check([UserGuard])
  }

  ngOnInit(): void {
    this.store.pipe(select(selectUsers), untilDestroy(this)).subscribe((users) => {
      this.users = lodash.keyBy(users, (user) => user.id)
    })
  }

  get arrItems() {
    return this.form.get('items') as FormArray
  }

  onAdd(index: number | null) {
    const form = createTaskChecklistItemForm(this.fb)

    if (index === null) {
      this.arrItems.push(form)
    } else {
      this.arrItems.insert(index, form)
    }
  }

  onRemove(index: number) {
    this.arrItems.removeAt(index)
  }

  get isEmpty() {
    return !this.arrItems.controls.length
  }

  hasError(index: number, name: string, error: string) {
    const control = this.arrItem(index, name)
    if (!control) return false
    return control.touched && control.hasError(error)
  }

  arrItem(index: number, name: string) {
    return this.arrItems.controls[index].get(name)
  }

  onChecklistChange(index: number, checked: boolean) {
    const checklistCtrl = this.arrItems.at(index)
    checklistCtrl.patchValue({
      date_completed: checked ? moment().unix() : null,
      completed_by: checked ? this.auth.user.id : null,
    })
  }

  onEnterChecklistItem($event: KeyboardEvent) {
    $event.preventDefault()
    ;($event.target as HTMLElement).blur()
    window.getSelection().removeAllRanges()
  }

  onClearChecklist() {
    this.confirm
      .confirm({
        title: $localize`Clear Checklist`,
        body: $localize`Are you sure you want to clear the checklist?`,
      })
      .subscribe(() => {
        this.arrItems.clear()
      })
  }

  onEnterPressed() {
    if (this.taskCtrl.value) {
      const form = createTaskChecklistItemForm(this.fb, { name: this.taskCtrl.value })
      this.arrItems.push(form)
      this.resetTask()
      // this.isAdding = false
    }
  }

  resetTask() {
    this.taskCtrl.patchValue('')
  }
}
